/**
 * @typedef {import('./index.js').Store<State, Getters>} Store
 */
/**
 * @template T
 * @typedef {import('./index.js').GetterFunction<T, State, Getters>} GetterFunction<T>
 */
/**
 * @template T
 * @template R
 * @typedef {import('./index.js').ActionFunction<T, R, State, Getters>} ActionFunction<T,R>
 */
/**
 * @typedef {import('./cart/cart').TaxesFees} TaxesFees
 */
/**
 * @typedef {import('./booking').QueryOptions} QueryOptions
 */
/**
 * @typedef Room
 * @property {string} synXisId
 * @property {Array<RoomStayRate>} rates
 * @property {number} numberOfUnits
 * @property {number} numberOfRates
 * @property {string} title
 * @property {string} roomDifferentiators
 * @property {string} shortDescription
 * @property {string} longDescription
 * @property {Array<ImageItem>} images
 * @property {string} maxGuests
 * @property {string} squareMeters
 * @property {string} squareFeet
 * @property {string} minimumStay
 * @property {string} roomType
 * @property {string} roomPageUrl
 * @property {Array<BedType>} bedType
 * @property {Array<Amenity>} amenities
 * @property {string} propertyId
 * @property {string} propertyLogoUrl
 * @property {string} propertyLogoAlt
 * @property {string} propertyName
 */
/**
 * @typedef ImageItem
 * @property {string} src
 * @property {string} alt
 */
/**
 * @typedef BedType
 * @property {string} title
 * @property {string} sleeps
 * @property {string} synXisId
 * @property {number} numberOfUnits
 * @property {boolean} accessible
 */
/**
 * @typedef Amenity
 * @property {string} title
 * @property {string} image
 */
/**
 * @typedef RoomStayRate
 * @property {string} title
 * @property {string} shortDescription
 * @property {string} longDescription
 * @property {string} image
 * @property {string} imageAltText
 * @property {string} termsAndConditions
 * @property {string} synXisId
 * @property {number} total
 * @property {number} taxes
 * @property {number} fees
 * @property {string} currencyCode
 * @property {string} displayPrice
 * @property {string} restrictionPolicy
 * @property {Array<NightlyRate>} nightlyRates
 * @property {Array<TaxesFees>} taxesFees
 */
/**
 * @typedef NightlyRate
 * @property {number} total
 * @property {number} taxes
 * @property {number} fees
 * @property {number} price
 * @property {string} date
 */
/**
 * @typedef BedTypeFacet
 * @property {string} id
 * @property {string} title
 */
/**
 * @typedef HighlightsFacet
 * @property {string} id
 * @property {string} title
 */
import axios from 'axios'
import {logger} from '@plugins/logger'
import {track} from '@plugins/tracking'
import {getApiUrl} from '@utils/api-helpers'
/**
 * @module Store:Rooms
 * @description Handle rooms availability features
 */
/**
 * Generates a unique id based on the number of adult and child guests
 * @param {{adultGuestCount: number, childGuestCount: number}} guests
 * @returns {string}
 */
const getQueryKey = (guests) => `a${
  guests.adultGuestCount
}c${
  guests.childGuestCount
}`
/**
 *
 * @param {Function} getDays - Function that returns the number of days based on the
 * @param {QueryOptions} query - Query options
 * @returns {Array<{hotelID: string, numNights: number, numChildren: number, checkin: Date, checkout: Date, daysToCheckIn: number}>}
 */
const getRoomsTracking = (getDays, query) => {
  return [{
      hotelID: query.hotelCode,
      numNights: getDays(query).toString(),
      numAdults: query.adultGuestCount.toString(),
      numChildren: query.childGuestCount.toString(),
      checkin: query.stayDateStart.split('T')[0],
      checkout: query.stayDateEnd.split('T')[0],
      daysToCheckIn: getDays(
        {
          stayDateStart: new Date().toISOString().split('T')[0],
          stayDateEnd: query.stayDateStart
        }
      ).toString()
    }]
}

export default {
  namespaced: true,
  /** Global State
   * @typedef {Object} State
   * @property {Object<string,Array<Room>>} rooms - Store [rooms]{@link Room} response from the check-availability calls, using the {@link getQueryKey} as key.
   * @property {Date} lastQuery - Date of the last query called
   * @property {boolean} loadingRooms - check-availability calling state
   * @property {Object<string,Array<string>>} synXisIds - Store roomsId's response from the check-availability calls, using the {@link getQueryKey} as key.
   * @property {Array<string>} requiredParams - Query parameters required to make the check-availability call
   * @property {Array<BedTypeFacet>} bedTypeFacets - Array of available Bed Type Facets for Room Filter component
   * @property {Array<HighlightsFacet>} highlightsFacets - Array of available Highlights Facets for Room Filter component
   * @property {Array<string>} selectedBedTypesFacets - Array of selected Bed Type Facets by the user in the Room Filter component
   * @property {Array<string>} selectedHighlightsFacets - Array of selected Highlights Facets by the user in the Room Filter component
   */
  /** @type {State} */
  state: {
    rooms: {},
    lastQuery: null,
    loadingRooms: false,
    synXisIds: {},
    requiredParams: [
      'hotelCode',
      'stayDateStart',
      'stayDateEnd',
      'adultGuestCount',
      'roomQuantity'
    ],
    hasCheckedAvailability: false,
    errorTypeCode: null,
    bedTypeFacets: [],
    highlightsFacets: [],
    selectedBedTypesFacets: [],
    selectedHighlightsFacets: [],
    currentCheckAvailabilityData: null
  },
  /** Global Getters
   * @typedef {Object} Getters
   * @property {GetterFunction<string>} currentQueryKey - Returns the {@link getQueryKey} for the current room
   * @property {GetterFunction<Array<Room>>} roomsAvailable - Returns the list of rooms available based on the {@link getQueryKey} for the current room
   * @property {GetterFunction<Array<string>>} roomsIds - Returns the list of roomsId's available based on the {@link getQueryKey} for the current room
   * @property {GetterFunction<{code: string, message: string}>} errorType - Returns an object with an availability error
   */
  /** @type {Getters} */
  getters: {
    currentQueryKey: (state, getters, rootState, rootGetters) => getQueryKey(rootGetters['booking/currentRoomGuests']),
    roomsAvailable: (state, getters) => state.rooms[getters.currentQueryKey] || [],
    roomsIds: (state, getters) => state.synXisIds[getters.currentQueryKey] || [],
    errorType: (state, getters, rootState) => {
      const query = rootState.booking.query
      const noRooms = getters.roomsAvailable.length === 0
      if (! noRooms) {
        return null
      } else if (query.promotionCode || query.roomCodes.length > 0 || query.rateCode || query.bedType) {
        return {code: 'noResultsError', message: 'No rooms available for specific search.'}
      } else {
        return {code: 'genericError', message: 'No rooms available.'}
      }
    },
    bedTypeFacets: (state) => {
      return state.bedTypeFacets || []
    },
    highlightsFacets: (state) => {
      return state.highlightsFacets || []
    },
    selectedBedTypesFacets: (state) => {
      return state.selectedBedTypesFacets || []
    },
    selectedHighlightsFacets: (state) => {
      return state.selectedHighlightsFacets || []
    },
  },
  /** Mutations */
  mutations: {
    'UPDATE_ROOMS' (state, {data, key, rootState, rootGetters}) {
      if (!data.roomStays) {
        state.rooms[key] = []
        state.synXisIds[key] = []
        return
      }
      const toSort = data.roomStays.map(room => {
        const sorted = room.rates.map(rate => {
          const nightlyRates = rate.nightlyRates || rate.ninightlyRates
          return {
            ...rate,
            nightlyRates
          }
        }).sort((a, b) => parseInt(a.total) < parseInt(b.total) ? -1 : 1)
        return {
          ...room,
          rates: sorted,
          bestRate: sorted[0]
        }
      })

      // Sorting the Room List in ascending order based on the best rate total from smallest to largest.
      toSort.sort(
        (a, b) => parseInt(a.bestRate.total) < parseInt(b.bestRate.total) ? -1 : 1
      )

      // Only for the Booking Edit Mode: Moving the Selected Room and Selected Rate to the start of the room list
      if (rootState.modifyReservation.isBookingEditMode && rootGetters["booking/currentRoom"]) {
        const selectedRoomIndex = toSort.findIndex(room => room.bedType.find((bedType) => bedType.synXisID === rootGetters["booking/currentRoom"]?.bedTypeCode));
        if (selectedRoomIndex !== -1) {
          const selectedRateIndex = toSort[selectedRoomIndex].rates.findIndex(rate => {
            return rate.synXisID === rootGetters["booking/currentRoom"]?.ratePlanCode &&
                    rate.roomTypeCode === rootGetters["booking/currentRoom"]?.bedTypeCode
          });

          if (selectedRateIndex !== -1) {
            const selectedRoomData = toSort[selectedRoomIndex];
            const selectedRateData = selectedRoomData.rates[selectedRateIndex];

            // Moving the Selected Rate to the start of the array
            selectedRoomData.rates.splice(selectedRateIndex, 1);
            selectedRoomData.rates.unshift({
              ...selectedRateData
            });

            // Moving the Selected Room to the start of the array
            toSort.splice(selectedRoomIndex, 1);
            toSort.unshift({
              ...selectedRoomData,
              rates: selectedRoomData.rates,
              bestRate: selectedRoomData.rates[0]
            });
          }
        }
      }

      state.rooms = {
        ...state.rooms,
        [key]: toSort
      }
      state.synXisIds = {
        ...state.synXisIds,
        [key]: data.synXisIds
      }
      state.bedTypeFacets = data.bedTypeFacets
      state.highlightsFacets = data.highlightsFacets
    },
    'UPDATE_LAST_QUERY' (state) {
      state.lastQuery = new Date()
    },
    'LOADING_ROOMS' (state, loading = false) {
      state.loadingRooms = loading
    },
    'RESET_ROOMS' (state) {
      state.rooms = {}
    },
    'UPDATE_HAS_CHECKED_AVAILABILITY' (state, hasCheckedAvailability) {
      state.hasCheckedAvailability = hasCheckedAvailability
    },
    'UPDATE_ERROR_TYPE_CODE' (state, errorTypeCode) {
      state.errorTypeCode = errorTypeCode
    },
    'UPDATE_SELECTED_BED_TYPES' (state, newValue) {
      state.selectedBedTypesFacets = newValue
    },
    'UPDATE_SELECTED_HIGHLIGHTS' (state, newValue) {
      state.selectedHighlightsFacets = newValue
    },
    'UPDATE_CURRENT_AVAILABILITY_DATA' (state, data) {
      state.currentCheckAvailabilityData = data
    }

  },
  /** Global Actions
   * @typedef {Object} Actions
   * @property {ActionFunction<undefined,void>} getAvailableRooms - Make the check-availability call and update the [rooms]{@link State} and [synXisIds]{@link State}
   * @property {ActionFunction<QueryOptions,boolean>} validateRequiredParams - Validate if the QueryOptions have all [required params]{@link State} setted
   * @property {ActionFunction<QueryOptions,boolean>} trackAvailabilityCall - Track availability call
   */
  /** @type {Actions} */
  actions: {
    getAvailableRooms(
      {
        state,
        commit,
        rootState,
        rootGetters,
        dispatch,
        getters
      }
    ) {
      const guests = rootGetters['booking/currentRoomGuests']
      const query = {
        ...rootState.booking.query,
        ...guests
      }
      if (query.roomCodes) {
        query.roomCodes = [...query.roomCodes]
        if (query.bedType) {
          query.roomCodes.push(query.bedType)
        }
        if (query.roomCodes.length === 0) {
          query.roomCodes = null
        }
      }
      if (rootState.modifyReservation.isBookingEditMode && !!rootState.modifyReservation.selectedReservations) {
        query.modify = {
          bedTypeId: rootState.modifyReservation.selectedReservations.map((reservation) => reservation.roomStays[0].roomTypes[0].roomTypeCode)
        }
      }
      query.primaryLanguageId = rootState?.global?.lang ?? 'en'
      if (state.hasCheckedAvailability) {
        query.promotionCode = ''
        query.groupCode = ''
        query.roomName = ''
        query.roomCodes = []
        query.rateCode = ''
        query.bedType = ''
      } else {
        commit('UPDATE_ERROR_TYPE_CODE', null)
      }
      commit('LOADING_ROOMS', true)
      dispatch('overlay/activateOverlay', 'check-availability', { root: true })
      return axios.post(getApiUrl('/api/v1/booking/check-room-availability'), query).then(({ data }) => {
         return dispatch('currency/changeBaseCurrency', {
          fromCurrency: data.currencyCode
        }, { root: true }).then(() => ({ data }))
      }).then(({ data }) => {
        commit('UPDATE_CURRENT_AVAILABILITY_DATA', data)
        commit('UPDATE_ROOMS', { data, key: getters.currentQueryKey, rootState, rootGetters })
        commit('UPDATE_SELECTED_BED_TYPES', [])
        commit('UPDATE_SELECTED_HIGHLIGHTS', [])

        const rateNotFound = query.rateCode && data.showMessageRateNotFound
        const hasNoRoomStays = !data.roomStays || data.roomStays.length === 0
        const shouldCheckAvailability = (rateNotFound || hasNoRoomStays) && !state.hasCheckedAvailability

        if (shouldCheckAvailability) {
          commit('UPDATE_HAS_CHECKED_AVAILABILITY', true)
          if (rateNotFound) {
            commit('UPDATE_ERROR_TYPE_CODE', 'noResultsError')
          } else {
            commit('UPDATE_ERROR_TYPE_CODE', getters.errorType.code)
            dispatch('getAvailableRooms')
          }
        } else if (data.roomStays) {
          commit('UPDATE_HAS_CHECKED_AVAILABILITY', false)
        }
        dispatch('trackAvailabilityCall')
      }).catch((err) => {
        commit('UPDATE_CURRENT_AVAILABILITY_DATA', null)
        logger.error(err)
        if (err.response.status === 400) {
          commit('UPDATE_ERROR_TYPE_CODE', getters.errorType.code)
          commit('RESET_ROOMS')
        }
        dispatch('trackAvailabilityCall', err.toJSON())
      }).finally(() => {
        commit('LOADING_ROOMS', false)
        commit('UPDATE_LAST_QUERY')
        dispatch('overlay/deactivateOverlay', 'check-availability', { root: true })
      })
    },
    validateRequiredParams(
      {
        state,
        rootState,
        rootGetters
      },
      data
    ) {
      const query = data || {
        ...rootState.booking.query,
        ...rootGetters['booking/currentRoomGuests']
      }
      const queryParams = Object.keys(query).filter(key => !! query[key])
      const requiredParamsAvailable = state.requiredParams.filter(f => queryParams.includes(f))
      return requiredParamsAvailable.length === state.requiredParams.length
    },
    trackAvailabilityCall(
      {
        rootGetters,
        getters
      },
      error
    ) {
      const bookingTracking = rootGetters['booking/getBookingTracking']()
      const route = rootGetters['booking/getRoute']()
      const query = rootGetters['booking/getQuery']
      const numRooms = getters.roomsAvailable.length.toString()
      bookingTracking.rooms = getRoomsTracking(rootGetters['booking/getDateRangeInDays'], query)
      bookingTracking.booking.availability = {
        numRooms
      }
      if (route.name.includes('rate')) {
        bookingTracking.events.ratesStep = true
      } else {
        bookingTracking.events.roomsStep = true
      }
      if (!getters.errorType && !error) {
        bookingTracking.events.availabilitySuccess = true
      } else {
        bookingTracking.events.availabilitySuccess = false
        bookingTracking.events.error = true
        error = error ? {
          code: error.code || 404,
          message: error.message
        } : getters.errorType
        bookingTracking.page.errors = [error]
      }
      if (!window.adobeDataLayer) {
        track(bookingTracking)
      }

    },
    reorderRoomsBySelection({state, getters, commit, rootState, rootGetters}) {
      if(state.currentCheckAvailabilityData !== null)
      commit('UPDATE_ROOMS', {data: state.currentCheckAvailabilityData, key: getters.currentQueryKey, rootState, rootGetters})
    }
  }
}
